// @ts-strict-ignore
import { Component, Input, OnInit } from '@angular/core';
import { PrintLabelingGenerator } from '@project/components/label/print-labeling/print-labeling-generator';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProjectModel } from '@project/model/project.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { StringUtils } from '@shared/util/string-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { PrintLabelingVO } from '@project/components/label/print-labeling/print-labeling.vo';
import { PrintLabelingDataService } from '../../label/print-labeling/print-labeling-data.service';
import { FileUtils } from '@shared/util/file-utils';
import { CustomValidators } from '@shared/custom-validators';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { IsProjectType } from '../../../model/is-project-type';

export enum ExportType {
  WSIX,
  WSCX,
}

@Component({
  standalone: false,
  selector: 'export-print-file',
  templateUrl: './export-print-file.component.html',
})
export class ExportPrintFileComponent implements OnInit {
  @Input()
  public exportType: ExportType;

  public saveOnServerPermission: boolean = false;

  public form: FormGroup;

  public isWSIXMode: boolean = false;

  constructor(
    private bsModalRef: BsModalRef,
    private formBuilder: FormBuilder,
    private alertHelper: AlertHelper,
    private projectModel: ProjectModel,
    private printLabelingDataService: PrintLabelingDataService,
    private localeResourceManager: LocaleResourceManager,
    private printLabelingGenerator: PrintLabelingGenerator,
  ) {}

  public ngOnInit(): void {
    this.isWSIXMode = this.exportType === ExportType.WSIX;
    this.saveOnServerPermission = this.projectModel.user.hasPermission(
      this.isWSIXMode ? "SAVE_WSIX_ON_SERVER" : "SAVE_WSCX_ON_SERVER"
    );
    const fileName = !this.projectModel.currentProject.articleNumberLong
      ? this.localeResourceManager.getValueByKey('main.label.default_project_name')
      : this.projectModel.currentProject.articleNumberLong.reducedArticleNumberLong;
    this.form = this.formBuilder.group({
      fileName: [
        this.sanitizeName(fileName),
        CustomValidators.defaultNameValidator(this.localeResourceManager, { weakValidation: true }),
      ],
      saveOnServer: [],
    });
    if (StringUtils.isNotEmpty(this.projectModel.currentProject.articleNumberLong?.reducedArticleNumberLong)) {
      this.form.controls.fileName.disable();
    }
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public export(): void {
    if (this.form.invalid) {
      return;
    }
    const fileName: string = this.sanitizeName(this.form.controls.fileName.value, true);
    let saveOnServer: boolean = false;
    if (this.projectModel.user.hasPermission("SAVE_WSIX_ON_SERVER")) {
      saveOnServer = !!this.form.controls.saveOnServer.value;
    }
    let userName: string = this.projectModel.user.surname;
    if (userName == null || userName.length < 1) {
      // just in case
      userName = this.projectModel.user.login;
    }

    const printLabelingVO: PrintLabelingVO = this.printLabelingGenerator.createPrintLabelingByProject(this.projectModel.currentProject);
    if (IsProjectType.markingSystem(printLabelingVO.project) && !this.isWSIXMode) {
      printLabelingVO.usePlotter = true;
    }
    const observable = this.isWSIXMode
      ? this.printLabelingDataService.exportToWSIX(printLabelingVO, userName, fileName, saveOnServer)
      : this.printLabelingDataService.exportToWSCX(printLabelingVO, fileName, saveOnServer);

    observable.subscribe(
      (data: string) => this.exportSuccess(data, this.sanitizeName(this.form.controls.fileName.value), saveOnServer),
      (eror) => this.exportFailure()
    );
  }

  private sanitizeName(fileName: string, addSuffix: boolean = false): string {
    return StringUtils.sanitizeFileName(fileName, addSuffix ? (this.isWSIXMode ? '.wsix' : '.wscx') : null);
  }

  private exportSuccess(data: string, fileName: string, saveOnServer: boolean): void {
    const successTitle: string = this.isWSIXMode ? 'exportToWSIX.title.success' : 'exportToWSCX.title.success';
    let successMessage: string = this.isWSIXMode ? 'exportToWSIX.message.success' : 'exportToWSCX.message.success';
    if (saveOnServer) {
      successMessage = this.isWSIXMode ? 'exportToWSIX.message.successSaveOnServer' : 'exportToWSCX.message.successSaveOnServer';
    }
    this.alertHelper.showAlert(successTitle, successMessage, null, [AlertButton.OK], "info").then(() => {
      if (!saveOnServer) {
        if (data) {
          FileUtils.saveTextFile(fileName, data, this.isWSIXMode ? '.wsix' : '.wscx');
        } else {
          this.exportFailure();
        }
      }
      this.close();
    });
  }

  private exportFailure(): void {
    const errorTitle: string = this.isWSIXMode ? 'exportToWSIX.title.error_exporting' : 'exportToWSCX.title.error_exporting';
    const errorMessage: string = this.isWSIXMode ? 'exportToWSIX.message.error_exporting' : 'exportToWSCX.message.error_exporting';
    this.alertHelper.showError(errorTitle, errorMessage);
  }
}
